"use client";

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PiDotsThreeOutlineVerticalFill,
  PiEye,
  PiPause,
  PiTrashSimple,
} from "react-icons/pi";
import { ActionIcon, Button, Box, Popover, Textarea } from "rizzui";

/**
 * MoreActions shows a menu for viewing, suspending, or deleting a campaign.
 * Suspensions and deletions prompt the user for a reason before calling callbacks.
 */
export function MoreActions({
  id,
  onSuspend,
  onDelete,
}: {
  id: string;
  onSuspend: (suspendReason: string) => void;
  onDelete: () => void;
}) {
  const navigate = useNavigate();

  // state for delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");

  // state for suspend dialog
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");

  const suspendReasons = [
    "Invalid verification document",
    "Money fraud",
    "Suspicious activity",
    "Spam campaign",
    "Inappropriate content",
  ];

  return (
    <>
      <Popover placement="bottom-end">
        <Popover.Trigger>
          <ActionIcon title="More Options" variant="text">
            <PiDotsThreeOutlineVerticalFill className="h-[18px] w-[18px] text-gray-500" />
          </ActionIcon>
        </Popover.Trigger>

        <Popover.Content className="z-10 min-w-[120px] px-2 py-2 bg-white">
          {({ setOpen }) => (
            <Box>
              <Button
                variant="text"
                className="flex justify-start w-full px-2 py-2 hover:bg-gray-100"
                onClick={() => {
                  navigate(`/campaign/campaign-details/${id}`);
                  setOpen(false);
                }}
              >
                <PiEye className="mr-2 h-5 w-5 text-gray-500" />
                View
              </Button>

              {/* Suspend */}
              <Button
                variant="text"
                className="flex w-full px-2 justify-start py-2 hover:bg-gray-100"
                onClick={() => {
                  setOpen(false);
                  setSuspendOpen(true);
                }}
              >
                <PiPause className="mr-2 h-5 w-5 text-gray-500" />
                Suspend
              </Button>

              {/* Delete */}
              <Button
                variant="text"
                color="danger"
                className="flex w-full px-2 py-2 justify-start hover:bg-red-100"
                onClick={() => {
                  setOpen(false);
                  setDeleteOpen(true);
                }}
              >
                <PiTrashSimple className="mr-2 h-5 w-5 text-red-500 " />
                Delete
              </Button>
            </Box>
          )}
        </Popover.Content>
      </Popover>

      {/* Delete confirmation dialog */}
      {deleteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Box className="bg-white p-6 rounded-md w-full max-w-sm">
            <h2 className="text-lg font-medium mb-4">
              Reason for deleting campaign
            </h2>
            <Textarea
              placeholder="Enter reason here…"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              className="w-full mb-4"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteReason("");
                  setDeleteOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                color="danger"
                onClick={() => {
                  console.log(
                    `Delete email: campaign ${id} reason: "${deleteReason}"`
                  );
                  onDelete();
                  setDeleteReason("");
                  setDeleteOpen(false);
                }}
              >
                Delete
              </Button>
            </div>
          </Box>
        </div>
      )}

      {/* Suspend confirmation dialog */}
      {suspendOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Box className="bg-white p-6 rounded-md w-full max-w-sm">
            <h2 className="text-lg font-medium mb-4">
              Reason for suspending campaign
            </h2>

            {/* Predefined reasons */}
            <div className="grid grid-cols-1 gap-2 mb-4">
              {suspendReasons.map((r) => (
                <Button
                  key={r}
                  variant={suspendReason === r ? "flat" : "outline"}
                  onClick={() => setSuspendReason(r)}
                >
                  {r}
                </Button>
              ))}
            </div>

            {/* Free-text override */}
            <Textarea
              placeholder="Or enter custom reason…"
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              className="w-full mb-4"
            />

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setSuspendReason("");
                  setSuspendOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                color="danger"
                onClick={() => {
                  console.log(
                    `Suspend email: campaign ${id} reason: "${suspendReason}"`
                  );
                  onSuspend(suspendReason.trim() || "No reason provided");
                  setSuspendReason("");
                  setSuspendOpen(false);
                }}
              >
                Suspend
              </Button>
            </div>
          </Box>
        </div>
      )}
    </>
  );
}
